import {
  Weekday,
  addDays,
  enumKeyOf,
  formatSpan,
  localDateOf,
  toLocalSpan,
  weekStartOf,
  type IsoDate,
  type WorkDaySummary,
} from '@mai-team-amusement/core';
import type { CommonTranslate } from '@mai-team-amusement/ui';
import { DateTime } from 'luxon';
import { z } from 'zod';

const SUNDAY_OFFSET = 6;
const NO_RECORD = '--:--';
const zWeekday = z.enum(Weekday);

/** Steps for previous / next week navigation. */
export const WeekStep = {
  Previous: -7,
  Next: 7,
} as const;

export const currentWeek = (timeZone: string) => weekStartOf(localDateOf(new Date(), timeZone));

/** The Monday of the week containing a date. */
export const mondayOf = (date: IsoDate) => weekStartOf(date);

export const shiftWeek = (weekStart: IsoDate, step: number) => addDays(weekStart, step);

/** `2026/10/12(Mon)` (weekday in the UI language). */
export function formatDayWithWeekday(date: IsoDate, common: CommonTranslate): string {
  const day = DateTime.fromISO(date);
  const weekday = common(`enum.WeekdayShort.${enumKeyOf(Weekday, zWeekday.parse(day.weekday))}`);
  return `${day.toFormat('yyyy/MM/dd')}(${weekday})`;
}

/** `2026/10/12 – 10/18`. */
export function formatWeekRange(weekStart: IsoDate): string {
  const start = DateTime.fromISO(weekStart);
  return `${start.toFormat('yyyy/MM/dd')} – ${start.plus({ days: SUNDAY_OFFSET }).toFormat('MM/dd')}`;
}

/** `2026/10/19(Mon) 12:00 (Asia/Tokyo)`. */
export function formatDeadline(iso: string, timeZone: string, common: CommonTranslate): string {
  const local = DateTime.fromISO(iso, { zone: timeZone });
  const date = local.toISODate() ?? '';
  return `${formatDayWithWeekday(date, common)} ${local.toFormat('HH:mm')} (${timeZone})`;
}

export function formatDateTime(iso: string, timeZone: string, locale: string): string {
  return DateTime.fromISO(iso, { zone: timeZone })
    .setLocale(locale)
    .toLocaleString(DateTime.DATETIME_MED);
}

/** `09:00-18:30`, `22:00-06:30(+1)`, `09:00-` (open) or `--:--` (no record). */
export function formatWorkTime(summary: WorkDaySummary | undefined): string {
  if (summary === undefined) {
    return NO_RECORD;
  }
  return formatSpan(
    toLocalSpan(
      summary.workDate,
      new Date(summary.startAt),
      summary.endAt === null ? null : new Date(summary.endAt),
      summary.timeZone,
    ),
  );
}
